import React, { useContext, useState, useEffect, useRef } from 'react';
import {
    Typography, Button, Alert, MenuItem, Select, FormControl, InputLabel,
    Box, Paper, Chip, TextField
} from '@mui/material';
import { PersonAdd, AddPhotoAlternate, Add } from '@mui/icons-material';
import { useAPI } from '../services/API';
import { UserContext } from '../services/UserContext';
import { useCommune } from '../services/CommuneContext';
import Input from '../components/Input';

const ROLES_ADMIN = [
    { value: 'lecteur', label: 'Lecteur' },
    { value: 'editeur', label: 'Éditeur' },
    { value: 'admin', label: 'Administrateur' },
];
const ROLES_SUPER = [
    ...ROLES_ADMIN,
    { value: 'super', label: 'Super-administrateur' },
];

function compressImage(file) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const maxSize = 300;
            let { width, height } = img;
            if (width > maxSize || height > maxSize) {
                if (width > height) { height = Math.round((height / width) * maxSize); width = maxSize; }
                else { width = Math.round((width / height) * maxSize); height = maxSize; }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png', 0.85));
        };
        img.src = URL.createObjectURL(file);
    });
}

export default function Parametres() {
    const [userContext] = useContext(UserContext);
    const commune = useCommune();
    const api = useAPI();

    const isAdmin = ['admin', 'super'].includes(userContext.role);
    const roles = userContext.role === 'super' ? ROLES_SUPER : ROLES_ADMIN;

    // Invitation
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('lecteur');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Blason
    const [blason, setBlason] = useState('');
    const [blasonDirty, setBlasonDirty] = useState(false);
    const [blasonSaving, setBlasonSaving] = useState(false);
    const [blasonSuccess, setBlasonSuccess] = useState(false);
    const [blasonError, setBlasonError] = useState('');
    const fileInputRef = useRef();

    // Durées concessions
    const [durees, setDurees] = useState([]);
    const [dureesDirty, setDureesDirty] = useState(false);
    const [newDuree, setNewDuree] = useState('');
    const [dureesSaving, setDureesSaving] = useState(false);
    const [dureesSuccess, setDureesSuccess] = useState(false);
    const [dureesError, setDureesError] = useState('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        api('GET', 'parametre').then(res => {
            if (!res.ok || !res.json) return;
            const map = {};
            res.json.forEach(p => { map[p.key] = p.value; });
            if (map.blason) setBlason(map.blason);
            if (map.durees_concessions) {
                try { setDurees(JSON.parse(map.durees_concessions)); } catch {}
            }
        });
    }, []);

    // — Invitation —
    const handleInviteSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccess(false);
        setError('');
        const res = await api('POST', 'utilisateur/inviter', { email, role, commune });
        setIsSubmitting(false);
        if (res.ok) {
            setSuccess(true);
            setEmail('');
            setRole('lecteur');
        } else {
            setError(res.json?.messageFr || 'Une erreur est survenue');
        }
    };

    // — Blason —
    const handleBlasonFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const compressed = await compressImage(file);
        setBlason(compressed);
        setBlasonDirty(true);
        setBlasonSuccess(false);
    };

    const handleBlasonSave = async () => {
        setBlasonSaving(true);
        setBlasonSuccess(false);
        setBlasonError('');
        const res = await api('PUT', 'parametre', { key: 'blason', value: blason });
        setBlasonSaving(false);
        if (res.ok) { setBlasonSuccess(true); setBlasonDirty(false); }
        else setBlasonError(res.json?.messageFr || 'Une erreur est survenue');
    };

    // — Durées —
    const addDuree = () => {
        const val = newDuree.trim();
        if (!val || durees.includes(val)) return;
        setDurees(prev => [...prev, val]);
        setNewDuree('');
        setDureesDirty(true);
        setDureesSuccess(false);
    };

    const removeDuree = (d) => {
        setDurees(prev => prev.filter(x => x !== d));
        setDureesDirty(true);
        setDureesSuccess(false);
    };

    const handleDureesSave = async () => {
        setDureesSaving(true);
        setDureesSuccess(false);
        setDureesError('');
        const res = await api('PUT', 'parametre', { key: 'durees_concessions', value: JSON.stringify(durees) });
        setDureesSaving(false);
        if (res.ok) { setDureesSuccess(true); setDureesDirty(false); }
        else setDureesError(res.json?.messageFr || 'Une erreur est survenue');
    };

    return (
        <>
            <Typography variant="h5" sx={{ textAlign: 'center' }}>Paramètres</Typography>

            {/* Blason */}
            {isAdmin && (
                <Paper sx={{ maxWidth: 480, mx: 'auto', mt: 4, p: 4, borderRadius: 3 }} elevation={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <AddPhotoAlternate color="primary" />
                        <Typography variant="h6">Blason de la commune</Typography>
                    </Box>

                    {blasonSuccess && <Alert severity="success" sx={{ mb: 2 }}>Blason mis à jour.</Alert>}
                    {blasonError && <Alert severity="error" sx={{ mb: 2 }}>{blasonError}</Alert>}

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        {blason ? (
                            <Box
                                component="img"
                                src={blason}
                                alt="Blason de la commune"
                                sx={{ maxHeight: 150, maxWidth: '100%', objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1 }}
                            />
                        ) : (
                            <Box sx={{ height: 100, width: '100%', border: '1px dashed', borderColor: 'divider', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="body2" color="text.secondary">Aucun blason défini</Typography>
                            </Box>
                        )}

                        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBlasonFile} />

                        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                            <Button variant="outlined" fullWidth onClick={() => fileInputRef.current.click()}>
                                Choisir une image
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={!blason || !blasonDirty || blasonSaving}
                                onClick={handleBlasonSave}
                            >
                                {blasonSaving ? 'Sauvegarde…' : 'Sauvegarder'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Durées des concessions */}
            {isAdmin && (
                <Paper sx={{ maxWidth: 480, mx: 'auto', mt: 4, p: 4, borderRadius: 3 }} elevation={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Typography variant="h6">Durées des concessions</Typography>
                    </Box>

                    {dureesSuccess && <Alert severity="success" sx={{ mb: 2 }}>Durées mises à jour.</Alert>}
                    {dureesError && <Alert severity="error" sx={{ mb: 2 }}>{dureesError}</Alert>}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 40 }}>
                        {durees.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">Aucune durée définie</Typography>
                        ) : (
                            durees.map(d => (
                                <Chip
                                    key={d}
                                    label={d}
                                    onDelete={() => removeDuree(d)}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            size="small"
                            label="Nouvelle durée"
                            value={newDuree}
                            onChange={e => setNewDuree(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDuree())}
                            placeholder="ex : 30 ans"
                            fullWidth
                        />
                        <Button variant="outlined" onClick={addDuree} sx={{ minWidth: 48, px: 1 }}>
                            <Add />
                        </Button>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        disabled={!dureesDirty || dureesSaving}
                        onClick={handleDureesSave}
                    >
                        {dureesSaving ? 'Sauvegarde…' : 'Sauvegarder'}
                    </Button>
                </Paper>
            )}

            {/* Invitation utilisateur */}
            <Paper sx={{ maxWidth: 480, mx: 'auto', mt: 4, p: 4, borderRadius: 3 }} elevation={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <PersonAdd color="primary" />
                    <Typography variant="h6">Inviter un utilisateur</Typography>
                </Box>

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Invitation envoyée avec succès à {email || 'l\'utilisateur'}.
                    </Alert>
                )}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <form onSubmit={handleInviteSubmit}>
                    <Input
                        name="Adresse email"
                        required
                        onChange={setEmail}
                        otherProps={{ fullWidth: true, type: 'email', value: email }}
                    />

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Rôle</InputLabel>
                        <Select value={role} label="Rôle" onChange={e => setRole(e.target.value)}>
                            {roles.map(r => (
                                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!email || isSubmitting}
                        sx={{ mt: 2, borderRadius: 2, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                    >
                        {isSubmitting ? 'Envoi en cours…' : 'Envoyer l\'invitation'}
                    </Button>
                </form>
            </Paper>
        </>
    );
}
